/**
 * StrategyImportDialog - Confirmation dialog for importing AI generation output.
 *
 * Shows import preview (block count, screen count) and warnings before executing.
 * Handles file parsing, validation, and delegates to StrategyImportService.
 *
 * @module strategy-blocks/components
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { StrategyImportService } from '../services/StrategyImportService';
import type { ValidatedImport, ImportResult } from '../services/StrategyImportService';
import type { FlowDocument } from '@segmently/schema-models/v2';

export interface StrategyImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** 'strategy' = import as multiple blocks, 'block' = import into specific block */
  mode: 'strategy' | 'block';
  projectId: string;
  strategyId: string;
  /** Block ID (required when mode='block') */
  blockId?: string;
  /** Theme FlowDocument for template merge */
  themeFlowDocument?: FlowDocument | null;
  /** Refresh canvas after import */
  onImported: (result: ImportResult) => void;
}

export const StrategyImportDialog: React.FC<StrategyImportDialogProps> = ({
  open,
  onClose,
  mode,
  projectId,
  strategyId,
  blockId,
  themeFlowDocument,
  onImported,
}) => {
  const [validated, setValidated] = useState<ValidatedImport | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<'file' | 'paste'>('file');
  const [jsonText, setJsonText] = useState('');

  const validateJson = useCallback((text: string) => {
    setError(null);
    setValidated(null);

    try {
      const data = JSON.parse(text);
      const result = StrategyImportService.validate(data);

      if (!result.valid) {
        setError(result.error || 'Invalid import data');
        return;
      }

      setValidated(result);
    } catch (err) {
      setError(`Failed to parse JSON: ${(err as Error).message}`);
    }
  }, []);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    validateJson(text);

    // Reset file input
    event.target.value = '';
  }, [validateJson]);

  const handlePasteValidate = useCallback(() => {
    if (!jsonText.trim()) {
      setError('Paste JSON text first');
      return;
    }
    validateJson(jsonText);
  }, [jsonText, validateJson]);

  const handleImport = useCallback(async () => {
    if (!validated) return;

    setImporting(true);
    setError(null);

    try {
      const service = new StrategyImportService();
      let result: ImportResult;

      if (mode === 'strategy') {
        result = await service.importStrategy(
          projectId,
          strategyId,
          validated.aiOutput,
          themeFlowDocument,
          { assetWorkspace: validated.assetWorkspace },
        );
      } else {
        if (!blockId) throw new Error('blockId required for block import');
        result = await service.importBlock(
          projectId,
          strategyId,
          blockId,
          validated.aiOutput,
          themeFlowDocument,
          { assetWorkspace: validated.assetWorkspace },
        );
      }

      if (result.errors.length > 0) {
        console.warn('[StrategyImportDialog] Import warnings:', result.errors);
      }

      onImported(result);
      handleClose();
    } catch (err) {
      setError(`Import failed: ${(err as Error).message}`);
    } finally {
      setImporting(false);
    }
  }, [validated, mode, projectId, strategyId, blockId, themeFlowDocument, onImported]);

  const handleClose = useCallback(() => {
    setValidated(null);
    setError(null);
    setImporting(false);
    setJsonText('');
    setInputMode('file');
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="strategy-import-dialog">
      <DialogTitle>
        Import {mode === 'strategy' ? 'Strategy' : 'Block'} from JSON
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Input mode toggle */}
          <ToggleButtonGroup
            value={inputMode}
            exclusive
            onChange={(_, v) => { if (v) { setInputMode(v); setValidated(null); setError(null); } }}
            size="small"
            fullWidth
          >
            <ToggleButton value="file" data-testid="strategy-import-file-toggle">File</ToggleButton>
            <ToggleButton value="paste" data-testid="strategy-import-paste-toggle">Paste JSON</ToggleButton>
          </ToggleButtonGroup>

          {/* File picker */}
          {inputMode === 'file' && (
            <Button variant="outlined" component="label" disabled={importing} data-testid="strategy-import-file-btn">
              Select JSON File
              <input
                type="file"
                hidden
                accept=".json"
                onChange={handleFileSelect}
              />
            </Button>
          )}

          {/* Paste textarea */}
          {inputMode === 'paste' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                multiline
                minRows={6}
                maxRows={16}
                placeholder='Paste AiGenerationOutput JSON here...\n{"onboardingStrategySequence": [...]}'
                value={jsonText}
                onChange={e => { setJsonText(e.target.value); setValidated(null); setError(null); }}
                disabled={importing}
                fullWidth
                data-testid="strategy-import-paste-input"
                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <Button
                variant="outlined"
                onClick={handlePasteValidate}
                disabled={importing || !jsonText.trim()}
                size="small"
                data-testid="strategy-import-validate-btn"
              >
                Validate JSON
              </Button>
            </Box>
          )}

          {/* Error */}
          {error && <Alert severity="error" data-testid="strategy-import-error">{error}</Alert>}

          {/* Validation preview */}
          {validated && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Alert severity="info" data-testid="strategy-import-validation-status">
                File validated successfully
              </Alert>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={`${validated.blockCount} block(s)`} color="primary" size="small" />
                <Chip label={`${validated.screenCount} screen(s)`} color="secondary" size="small" />
                {validated.assetWorkspace === 'portable' && (
                  <Chip label="portable assets" color="success" size="small" />
                )}
              </Box>

              {mode === 'block' && (
                <Alert severity="warning" variant="outlined">
                  Existing screens in this block will be replaced.
                </Alert>
              )}

              {themeFlowDocument ? (
                <Typography variant="caption" color="text.secondary">
                  Screens will be enriched with strategy theme templates.
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No theme found — screens will use default templates.
                </Typography>
              )}
            </Box>
          )}

          {importing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Importing...</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!validated || importing}
          data-testid="strategy-import-submit-btn"
        >
          {importing ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
